import { motion } from 'framer-motion';
import { ArrowRight, Play, TrendingUp } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { Button } from '@/components/ui/button';

const DJANGO_BASE_URL = 'https://agritrack.samperlabs.com';

export const Hero = () => {
  const { t } = useI18n();

  return (
    <section
      className="relative min-h-screen pt-24 lg:pt-32 pb-16 lg:pb-24 overflow-hidden bg-surface"
      data-testid="hero-section"
    >
      {/* Noise overlay for texture */}
      <div className="absolute inset-0 noise-overlay pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="space-y-8"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <span
                className="inline-flex items-center px-4 py-2 rounded-full bg-forest-100 text-forest-800 text-xs font-mono uppercase tracking-[0.15em]"
                data-testid="hero-badge"
              >
                {t('hero.badge')}
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-primary leading-[1.1] tracking-tight"
              data-testid="hero-title"
            >
              {t('hero.title')}{' '}
              <span className="inline-flex items-center gap-2 px-3 py-1 bg-forest-50 border border-forest-200 rounded-lg font-mono text-3xl sm:text-4xl lg:text-5xl tracking-normal text-forest-700 shadow-sm transform -rotate-1">
                <svg className="w-6 h-6 lg:w-8 lg:h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <line x1="3" y1="9" x2="21" y2="9" />
                  <line x1="9" y1="3" x2="9" y2="21" />
                </svg>
                {t('hero.titleHighlight')}
              </span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-lg lg:text-xl text-muted-foreground max-w-xl leading-relaxed"
              data-testid="hero-subtitle"
            >
              {t('hero.subtitle')}
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 pt-4"
            >
              <a href={`${DJANGO_BASE_URL}/register/`} data-testid="hero-cta-primary">
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-gradient-to-r from-primary to-primary-light text-white font-semibold px-8 py-6 text-base rounded-xl shadow-elevated hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                >
                  {t('hero.cta')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <Button
                variant="outline"
                size="lg"
                className="w-full sm:w-auto border-2 border-primary/20 text-primary font-semibold px-8 py-6 text-base rounded-xl hover:bg-primary/5 hover:border-primary/30 transition-all duration-300"
                data-testid="hero-cta-secondary"
              >
                <Play className="w-5 h-5 mr-2" />
                {t('hero.watchDemo')}
              </Button>
            </motion.div>
          </motion.div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, x: 50, rotate: 3 }}
            animate={{ opacity: 1, x: 0, rotate: 2 }}
            transition={{ delay: 0.3, duration: 0.8, ease: 'easeOut' }}
            className="relative"
          >
            <div className="relative bg-white rounded-2xl shadow-elevated border border-primary/10 overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1766367245034-b66ade68fef0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBkYWlyeSUyMGZhcm0lMjBjYXR0bGV8ZW58MHx8fHwxNzc0MjkwNDUyfDA&ixlib=rb-4.1.0&q=85&w=800"
                alt="Modern dairy farm with cattle grazing at sunset"
                className="w-full h-auto object-cover"
                data-testid="hero-image"
              />
              
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent pointer-events-none" />
            </div>

            {/* Floating stat card */}
            <motion.div
              initial={{ opacity: 0, y: 20, x: -20 }}
              animate={{ opacity: 1, y: 0, x: 0 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="absolute -bottom-6 -left-6 bg-primary text-white p-5 rounded-xl shadow-elevated hidden md:block"
              data-testid="hero-stat-card"
            >
              <TrendingUp className="w-8 h-8 mb-2 text-forest-300" />
              <p className="text-sm font-medium text-forest-200">Real-time Herd Efficiency</p>
              <p className="text-2xl font-bold mt-1">+14.2%</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
